import {
    formatDate,
    formatDateTime,
    formatBool,
    formatEnumLabel,
    ENTITY_AUDIT_FIELDS,
    DATE_PRIMARY_FIELDS,
} from "components/EntityFramework/formatters";

/*
 * Travel trip-entity definitions, rendered by the generic EntityList /
 * EntityDetail (components/EntityFramework) through TripEntityList and
 * TripEntityDetail.
 */

const formatMoney = (currencyField) => (value, data) => {
    if (value == null || value === "") return "-";
    const currency = data?.[currencyField] || "";
    return `${currency} ${value}`.trim();
};

const formatMoneyPair = (item) => {
    if (item?.addon_price == null && item?.order_total == null && item?.unit_price == null) return null;
    const amount = item.addon_price ?? item.order_total ?? item.unit_price;
    const currency = item.currency || "";
    return `${currency} ${amount}`.trim();
};

/** Kept for importers; the framework's audit block is the same list. */
export const TRIP_ENTITY_AUDIT_FIELDS = ENTITY_AUDIT_FIELDS;

/** Fields whose header title should show a date-only label (not raw ISO). */
export const TRIP_ENTITY_DATE_PRIMARY_FIELDS = DATE_PRIMARY_FIELDS;

export { formatDate, formatDateTime };

export const resolveNumericTripId = (row) => {
    const raw = row?.trip_id;
    if (raw != null && raw !== "") {
        const id = Number(raw);
        if (Number.isInteger(id) && id > 0) return id;
    }
    const fromList = Array.isArray(row?.trip_ids) && row.trip_ids.length
        ? Number(row.trip_ids[0])
        : null;
    return Number.isInteger(fromList) && fromList > 0 ? fromList : null;
};

const destinationLabel = (row) =>
    row?.destination
    || row?.primary_country
    || row?.trip_title
    || (Array.isArray(row?.trip_titles) && row.trip_titles[0])
    || "-";

const tripLinkCell = (navigate, wizardStep) => ({ row }) => {
    const tripId = resolveNumericTripId(row.original);
    const packageId = Number(row.original?.trip_package_id ?? row.original?.package_id);
    const label = destinationLabel(row.original);
    if (!tripId) {
        return (
            <span style={{ color: "inherit", fontWeight: 400, fontSize: "0.75rem" }}>
                {label}
            </span>
        );
    }
    const go = () => {
        // Prefer package-scoped edit so the correct package's departures/services load.
        const base = Number.isInteger(packageId) && packageId > 0
            ? `/trips/${tripId}/packages/${packageId}/edit`
            : `/trips/${tripId}/edit`;
        navigate(`${base}?step=${wizardStep}`);
    };
    return (
        <span
            role="button"
            tabIndex={0}
            onClick={go}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") go();
            }}
            style={{ cursor: "pointer", color: "#1565C8", fontWeight: 500, fontSize: "0.75rem" }}
        >
            {label}
        </span>
    );
};

/** Opens trip detail page (not edit wizard). */
const tripDetailLinkCell = (navigate) => ({ row }) => {
    const tripId = resolveNumericTripId(row.original);
    const label = destinationLabel(row.original);
    if (!tripId) {
        return (
            <span style={{ color: "inherit", fontWeight: 400, fontSize: "0.75rem" }}>
                {label}
            </span>
        );
    }
    const go = () => navigate(`/trips/detail/${tripId}`);
    return (
        <span
            role="button"
            tabIndex={0}
            onClick={go}
            onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") go();
            }}
            style={{ cursor: "pointer", color: "#1565C8", fontWeight: 500, fontSize: "0.75rem" }}
        >
            {label}
        </span>
    );
};

export const TRIP_ENTITY_CONFIG = {
    "trip-packages": {
        title: "Trip Package",
        subtitle: "Package and pricing records across all trips.",
        listKey: "trip-packages",
        wizardStep: "packages",
        addLabel: "Add Package",
        dialogTitle: "Add Package",
        route: "/trip-packages",
        objectType: "trip-package",
        detailApiPath: "/trip-packages",
        primaryField: "title",
        supportsDelete: false,
        detailFields: [
            { label: "Package Title", field: "title" },
            { label: "Trip", field: "trip_title" },
            { label: "Base Price", field: "base_price", renderValue: formatMoney("currency") },
            { label: "Duration", field: "duration_days", renderValue: (v) => (v ? `${v} days` : "-") },
            { label: "City", field: "city" },
            { label: "Tier", field: "package_tier", renderValue: formatEnumLabel },
            { label: "Sequence", field: "sequence" },
            { label: "Status", field: "status" },
            { label: "Description", field: "description", fullWidth: true, html: true },
        ],
        relatedRecords: [
            {
                key: "components",
                title: "Trip Logistics",
                emptyMessage: "No trip logistics linked to this package.",
                primaryField: "name",
                secondaryField: "category",
                getSecondary: (item) =>
                    item.category ? formatEnumLabel(item.category) : item.sub_category
                        ? formatEnumLabel(item.sub_category)
                        : null,
                getDetailPath: (id) => `/trip-package-components/${id}`,
                allowAdd: true,
                addStep: "services",
            },
            {
                key: "payment_templates",
                title: "Payment Templates",
                emptyMessage: "No payment templates linked to this package.",
                primaryField: "template_name",
                secondaryField: "payment_type",
                getSecondary: (item) =>
                    item.payment_type ? formatEnumLabel(item.payment_type) : null,
                getDetailPath: (id) => `/payment-templates/${id}`,
                allowAdd: true,
                addStep: "pricing",
            },
            {
                key: "departure_dates",
                title: "Departure Dates",
                emptyMessage: "No departure dates linked to this package.",
                primaryField: "title",
                getSecondary: (item) =>
                    item.departure_city
                        ? item.departure_city
                        : item.return_date
                          ? `Return ${formatDate(item.return_date)}`
                          : null,
                getDetailPath: (id) => `/trip-departure-dates/${id}`,
                allowAdd: true,
                addStep: "departures",
            },
            {
                key: "merchant_allocations",
                title: "Merchant Allocations",
                emptyMessage: "No merchant allocations linked to this package.",
                primaryField: "name",
                getDetailPath: (id) => `/merchant-allocations/${id}`,
                allowAdd: true,
                addStep: "allocations",
            },
            {
                key: "itineraries",
                title: "Itinerary",
                emptyMessage: "No itinerary linked to this package.",
                primaryField: "title",
                getSecondary: (item) => (item.total_days != null ? `${item.total_days} days` : null),
                getDetailPath: (id) => `/trip-itineraries/${id}`,
                allowAdd: true,
                addStep: "itineraries",
            },
            {
                key: "addons",
                title: "Add-ons",
                emptyMessage: "No add-ons linked to this package.",
                primaryField: "name",
                getSecondary: formatMoneyPair,
                getDetailPath: (id) => `/trip-addons/${id}`,
                allowAdd: true,
                addStep: "addons",
            },
            {
                key: "faqs",
                title: "FAQ",
                emptyMessage: "No FAQs linked to this package.",
                primaryField: "question",
                getDetailPath: (id) => `/trip-faqs/${id}`,
                allowAdd: true,
                addStep: "faqs",
                showStatus: false,
                showPublished: true,
            },
        ],
        columns: (navigate) => [
            { Header: "Package", accessor: "title", width: "20%" },
            {
                Header: "Trip",
                accessor: "trip_title",
                width: "18%",
                Cell: tripLinkCell(navigate, "packages"),
            },
            {
                Header: "Base Price",
                accessor: "base_price",
                width: "12%",
                align: "right",
                Cell: ({ value, row }) => (
                    <span style={{ fontFamily: "monospace" }}>
                        {value != null ? `${row.original.currency || ""} ${value}`.trim() : "-"}
                    </span>
                ),
            },
            { Header: "Duration", accessor: "duration_days", width: "10%", Cell: ({ value }) => (value ? `${value} days` : "-") },
            { Header: "Status", accessor: "status", width: "12%" },
            { Header: "Created", accessor: "created_at", width: "14%", Cell: ({ value }) => formatDate(value) },
        ],
    },
    "package-components": {
        title: "Trip Logistics",
        subtitle: "Operational trip logistics linked to trip packages.",
        listKey: "package-components",
        wizardStep: "services",
        addLabel: "Add Trip Logistics",
        dialogTitle: "Add Trip Logistics",
        route: "/trip-package-components",
        objectType: "package-component",
        detailApiPath: "/package-components",
        primaryField: "name",
        supportsDelete: false,
        detailFields: [
            { label: "Trip Logistics Name", field: "name" },
            { label: "Trip", field: "trip_title" },
            { label: "Package", field: "package_title" },
            { label: "Category", field: "category", renderValue: formatEnumLabel },
            { label: "Sub Category", field: "sub_category", renderValue: formatEnumLabel },
            { label: "Cost Price", field: "cost_price", renderValue: formatMoney("currency") },
            { label: "Status", field: "status" },
            { label: "Description", field: "description", fullWidth: true, html: true },
        ],
        relatedRecords: [
            {
                key: "line_items",
                title: "Itinerary Day Line Items",
                emptyMessage: "No day line items linked to this trip logistics record.",
                primaryField: "title",
                getSecondary: (item) =>
                    item.sequence != null ? `Sequence ${item.sequence}` : item.type || null,
                getDetailPath: (id) => `/trip-itinerary-day-line-items/${id}`,
                allowAdd: true,
                addStep: "itineraries",
            },
            {
                key: "addons",
                title: "Add-ons",
                emptyMessage: "No add-ons linked to this trip logistics record.",
                primaryField: "name",
                getSecondary: formatMoneyPair,
                getDetailPath: (id) => `/trip-addons/${id}`,
                allowAdd: true,
                addStep: "addons",
            },
            {
                key: "merchant_allocations",
                title: "Merchant Allocations",
                emptyMessage: "No merchant allocations linked to this trip logistics record.",
                primaryField: "name",
                getDetailPath: (id) => `/merchant-allocations/${id}`,
                allowAdd: true,
                addStep: "allocations",
            },
            {
                key: "merchant_order_lines",
                title: "Merchant Order Lines",
                emptyMessage: "No merchant order lines linked to this trip logistics record.",
                primaryField: "name",
                getDetailPath: (id) => `/merchant-order-line/${id}`,
                allowAdd: false,
            },
        ],
        columns: (navigate) => [
            { Header: "Trip Logistics", accessor: "name", width: "20%" },
            {
                Header: "Trip",
                accessor: "trip_title",
                width: "16%",
                Cell: tripLinkCell(navigate, "services"),
            },
            { Header: "Package", accessor: "package_title", width: "16%" },
            {
                Header: "Category",
                accessor: "category",
                width: "14%",
                Cell: ({ value }) => formatEnumLabel(value),
            },
            {
                Header: "Sub Category",
                accessor: "sub_category",
                width: "14%",
                Cell: ({ value }) => formatEnumLabel(value),
            },
            { Header: "Status", accessor: "status", width: "10%" },
            { Header: "Created", accessor: "created_at", width: "10%", Cell: ({ value }) => formatDate(value) },
        ],
    },
    "departure-dates": {
        title: "Departure Dates",
        subtitle: "Scheduled departures and allocation for all trips.",
        listKey: "departure-dates",
        wizardStep: "departures",
        addLabel: "Add Departure",
        dialogTitle: "Add Departure Date",
        route: "/trip-departure-dates",
        objectType: "departure-date",
        detailApiPath: "/departure-dates",
        primaryField: "departure_date",
        supportsDelete: true,
        detailFields: [
            { label: "Destination", field: "trip_title", linkToTrip: true },
            { label: "Package", field: "package_title" },
            { label: "Departure Date", field: "departure_date", renderValue: formatDate },
            { label: "Return Date", field: "return_date", renderValue: formatDate },
            { label: "Departure City", field: "departure_city" },
            { label: "Total Allocation", field: "total_allocation" },
            { label: "Minimum Pax", field: "minimum_pax" },
            { label: "Maximum Pax", field: "maximum_pax" },
            { label: "Book By Date", field: "book_by_date", renderValue: formatDate },
            { label: "Status", field: "status" },
        ],
        relatedRecords: [
            {
                key: "orders",
                title: "Orders",
                emptyMessage: "No orders linked to this departure date.",
                primaryField: "name",
                getSecondary: (item) =>
                    item.order_total != null
                        ? `Total ${item.order_total}`
                        : item.payment_status || null,
                getDetailPath: (id) => `/travel-order/${id}`,
                allowAdd: false,
            },
            {
                key: "merchant_allocations",
                title: "Merchant Allocations",
                emptyMessage: "No merchant allocations linked to this departure date.",
                primaryField: "name",
                getDetailPath: (id) => `/merchant-allocations/${id}`,
                allowAdd: true,
                addStep: "allocations",
            },
        ],
        columns: (navigate) => [
            { Header: "Departure", accessor: "departure_date", width: "16%", Cell: ({ value }) => formatDate(value) },
            {
                Header: "Destination",
                accessor: "trip_title",
                width: "20%",
                Cell: tripLinkCell(navigate, "departures"),
            },
            { Header: "Return", accessor: "return_date", width: "16%", Cell: ({ value }) => formatDate(value) },
            { Header: "City", accessor: "departure_city", width: "14%" },
            { Header: "Allocation", accessor: "total_allocation", width: "12%", align: "right" },
            { Header: "Status", accessor: "status", width: "12%" },
            { Header: "Created", accessor: "created_at", width: "10%", Cell: ({ value }) => formatDate(value) },
        ],
    },
    itineraries: {
        title: "Itineraries",
        subtitle: "Trip itinerary blocks from the itineraries wizard step.",
        listKey: "itineraries",
        wizardStep: "itineraries",
        addLabel: "Add Itinerary",
        dialogTitle: "Add Itinerary",
        route: "/trip-itineraries",
        objectType: "itinerary",
        detailApiPath: "/itineraries",
        primaryField: "title",
        supportsDelete: true,
        detailFields: [
            { label: "Itinerary Title", field: "title" },
            { label: "Trip", field: "trip_title" },
            { label: "Total Days", field: "total_days" },
            { label: "Primary Attractions", field: "primary_attractions" },
            { label: "Meal Plan", field: "meal_plan" },
            { label: "Status", field: "status" },
            { label: "Description", field: "description", fullWidth: true, html: true },
        ],
        relatedRecords: [
            {
                key: "itinerary_days",
                title: "Itinerary Days",
                emptyMessage: "No itinerary days linked to this itinerary.",
                primaryField: "title",
                getSecondary: (item) =>
                    item.day_number != null ? `Day ${item.day_number}` : null,
                getDetailPath: (id) => `/trip-itinerary-days/${id}`,
                allowAdd: true,
                addStep: "itineraries",
                showStatus: false,
            },
        ],
        columns: (navigate) => [
            { Header: "Itinerary", accessor: "title", width: "22%" },
            {
                Header: "Trip",
                accessor: "trip_title",
                width: "18%",
                Cell: tripLinkCell(navigate, "itineraries"),
            },
            { Header: "Days", accessor: "total_days", width: "10%", align: "right" },
            { Header: "Attractions", accessor: "primary_attractions", width: "20%" },
            { Header: "Status", accessor: "status", width: "12%" },
            { Header: "Created", accessor: "created_at", width: "12%", Cell: ({ value }) => formatDate(value) },
        ],
    },
    "itinerary-days": {
        title: "Itinerary Days",
        subtitle: "Daily schedule entries within trip itineraries.",
        listKey: "itinerary-days",
        wizardStep: "itineraries",
        addLabel: "Add Itinerary Day",
        dialogTitle: "Add Itinerary Day",
        route: "/trip-itinerary-days",
        objectType: "itinerary-day",
        detailApiPath: "/itinerary-days",
        primaryField: "day_title",
        supportsDelete: true,
        detailFields: [
            { label: "Trip", field: "trip_title" },
            { label: "Itinerary", field: "itinerary_title" },
            { label: "Day Number", field: "day_number" },
            { label: "Day Title", field: "day_title" },
            { label: "Description", field: "description", fullWidth: true, html: true },
        ],
        relatedRecords: [
            {
                key: "line_items",
                title: "Itinerary Day Line Items",
                emptyMessage: "No day line items linked to this itinerary day.",
                primaryField: "title",
                getSecondary: (item) =>
                    item.sequence != null
                        ? `Sequence ${item.sequence}${item.type ? ` · ${item.type}` : ""}`
                        : item.type || null,
                getDetailPath: (id) => `/trip-itinerary-day-line-items/${id}`,
                allowAdd: true,
                addStep: "itineraries",
                showStatus: false,
            },
        ],
        columns: (navigate, handlers = {}) => [
            { Header: "Title", accessor: "day_title", width: "22%" },
            {
                Header: "Trip",
                accessor: "trip_title",
                width: "18%",
                Cell: tripDetailLinkCell(navigate),
            },
            { Header: "Itinerary", accessor: "itinerary_title", width: "18%" },
            { Header: "Day", accessor: "day_number", width: "8%" },
            {
                Header: "Description",
                accessor: "description",
                width: "24%",
                Cell: ({ value }) => (
                    <span
                        style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                            display: "inline-block",
                            maxWidth: "100%",
                        }}
                    >
                        {value || "-"}
                    </span>
                ),
            },
            { Header: "Created", accessor: "created_at", width: "10%", Cell: ({ value }) => formatDate(value) },
            {
                Header: "Actions",
                accessor: "actions",
                width: "10%",
                align: "center",
                disableSortBy: true,
                Cell: ({ row }) => handlers?.renderActionMenu
                    ? handlers.renderActionMenu(row.original)
                    : "-",
            },
        ],
    },
    "itinerary-day-line-items": {
        title: "Itinerary Day Line Items",
        subtitle: "Line items scheduled within itinerary days.",
        listKey: "itinerary-day-line-items",
        wizardStep: "itineraries",
        addLabel: "Add Line Item",
        dialogTitle: "Add Itinerary Day Line Item",
        route: "/trip-itinerary-day-line-items",
        objectType: "itinerary-day-line-item",
        detailApiPath: "/itinerary-day-line-items",
        primaryField: "title",
        supportsDelete: true,
        detailFields: [
            { label: "Title", field: "title" },
            { label: "Trip", field: "trip_title" },
            { label: "Itinerary", field: "itinerary_title" },
            { label: "Day", field: "day_number" },
            { label: "Type", field: "type" },
            { label: "Mandatory", field: "mandatory", renderValue: formatBool },
            { label: "Sequence", field: "sequence" },
            { label: "Description", field: "description", fullWidth: true, html: true },
        ],
        relatedRecords: [
            {
                key: "addons",
                title: "Add-ons",
                emptyMessage: "No add-ons linked to this day line item.",
                primaryField: "name",
                getSecondary: formatMoneyPair,
                getDetailPath: (id) => `/trip-addons/${id}`,
                allowAdd: true,
                addStep: "addons",
            },
            {
                key: "merchant_order_lines",
                title: "Merchant Order Lines",
                emptyMessage: "No merchant order lines linked to this day line item.",
                primaryField: "name",
                getDetailPath: (id) => `/merchant-order-line/${id}`,
                allowAdd: false,
            },
        ],
        columns: (navigate) => [
            { Header: "Title", accessor: "title", width: "20%" },
            {
                Header: "Trip",
                accessor: "trip_title",
                width: "16%",
                Cell: tripLinkCell(navigate, "itineraries"),
            },
            { Header: "Itinerary", accessor: "itinerary_title", width: "16%" },
            { Header: "Day", accessor: "day_number", width: "8%" },
            { Header: "Type", accessor: "type", width: "12%" },
            {
                Header: "Mandatory",
                accessor: "mandatory",
                width: "10%",
                Cell: ({ value }) => (value ? "Yes" : "No"),
            },
            { Header: "Created", accessor: "created_at", width: "12%", Cell: ({ value }) => formatDate(value) },
        ],
    },
    addons: {
        title: "Addons",
        subtitle: "Trip addons including services, activities, and extensions.",
        listKey: "addons",
        wizardStep: "addons",
        addLabel: "Add Addon",
        dialogTitle: "Add Addon",
        route: "/trip-addons",
        objectType: "addon",
        detailApiPath: "/addons",
        primaryField: "name",
        supportsDelete: true,
        detailFields: [
            { label: "Addon Name", field: "name" },
            { label: "Trip", field: "trip_title" },
            { label: "Type", field: "type" },
            { label: "Price", field: "addon_price", renderValue: formatMoney("currency") },
            { label: "Application Scope", field: "application_scope" },
            { label: "Status", field: "status" },
            { label: "Website Title", field: "website_title" },
            { label: "Website Description", field: "website_description", fullWidth: true, html: true },
        ],
        relatedRecords: [
            {
                key: "itinerary_days",
                title: "Itinerary Days",
                emptyMessage: "No itinerary days linked to this add-on.",
                primaryField: "title",
                getSecondary: (item) =>
                    item.day_number != null ? `Day ${item.day_number}` : null,
                getDetailPath: (id) => `/trip-itinerary-days/${id}`,
                allowAdd: false,
                showStatus: false,
            },
        ],
        columns: (navigate) => [
            { Header: "Addon", accessor: "name", width: "20%" },
            {
                Header: "Trip",
                accessor: "trip_title",
                width: "18%",
                Cell: tripLinkCell(navigate, "addons"),
            },
            { Header: "Type", accessor: "type", width: "14%" },
            {
                Header: "Price",
                accessor: "addon_price",
                width: "12%",
                align: "right",
                Cell: ({ value, row }) => (
                    <span style={{ fontFamily: "monospace" }}>
                        {value != null ? `${row.original.currency || ""} ${value}`.trim() : "-"}
                    </span>
                ),
            },
            { Header: "Scope", accessor: "application_scope", width: "14%" },
            { Header: "Status", accessor: "status", width: "10%" },
            { Header: "Created", accessor: "created_at", width: "12%", Cell: ({ value }) => formatDate(value) },
        ],
    },
    faqs: {
        title: "FAQs",
        subtitle: "Frequently asked questions linked to trips.",
        listKey: "faqs",
        wizardStep: "faqs",
        addLabel: "Add FAQ",
        dialogTitle: "Add FAQ",
        route: "/trip-faqs",
        objectType: "faq",
        detailApiPath: "/faqs",
        primaryField: "question",
        supportsDelete: true,
        detailFields: [
            { label: "Trip", field: "trip_title" },
            { label: "Linked Package", field: "package_title" },
            { label: "Question", field: "question", fullWidth: true },
            { label: "Answer", field: "answer", fullWidth: true, html: true },
            { label: "Published", field: "is_published", renderValue: formatBool },
        ],
        relatedRecords: [
            {
                key: "packages",
                title: "Package",
                emptyMessage: "No package linked to this FAQ.",
                primaryField: "title",
                getSecondary: (item) =>
                    item.duration_days != null ? `${item.duration_days} days` : null,
                getChipLabel: (item) =>
                    item.package_tier ? formatEnumLabel(item.package_tier) : null,
                getDetailPath: (id) => `/trip-packages/${id}`,
                allowAdd: false,
            },
        ],
        columns: (navigate) => [
            {
                Header: "Question",
                accessor: "question",
                width: "22%",
                Cell: ({ value }) => (
                    <span
                        style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                            display: "inline-block",
                            maxWidth: "100%",
                        }}
                    >
                        {value || "-"}
                    </span>
                ),
            },
            {
                Header: "Trip",
                accessor: "trip_title",
                width: "14%",
                Cell: tripLinkCell(navigate, "faqs"),
            },
            {
                Header: "Linked Package",
                accessor: "package_title",
                width: "14%",
                Cell: ({ value }) => (
                    <span
                        style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                            display: "inline-block",
                            maxWidth: "100%",
                        }}
                    >
                        {value || "-"}
                    </span>
                ),
            },
            {
                Header: "Answer",
                accessor: "answer",
                width: "22%",
                Cell: ({ value }) => (
                    <span
                        style={{
                            whiteSpace: "normal",
                            wordBreak: "break-word",
                            overflowWrap: "anywhere",
                            lineHeight: 1.35,
                            display: "inline-block",
                            maxWidth: "100%",
                        }}
                    >
                        {value || "-"}
                    </span>
                ),
            },
            {
                Header: "Published",
                accessor: "is_published",
                width: "8%",
                Cell: ({ value }) => (value ? "Yes" : "No"),
            },
            { Header: "Created", accessor: "created_at", width: "10%", Cell: ({ value }) => formatDate(value) },
            {
                Header: "Modified",
                accessor: "updated_at",
                width: "10%",
                Cell: ({ value, row }) => formatDate(value || row.original.created_at),
            },
        ],
    },
    "merchant-allocations": {
        title: "Merchant Allocation",
        subtitle: "Merchant allocations linked to trip logistics and departure dates.",
        listKey: "merchant-allocations",
        wizardStep: "allocations",
        addLabel: "Add Allocation",
        dialogTitle: "Add Merchant Allocation",
        route: "/merchant-allocations",
        objectType: "merchant-allocation",
        detailApiPath: "/merchant-allocations",
        primaryField: "name",
        supportsDelete: true,
        detailFields: [
            { label: "Name", field: "name" },
            { label: "Destination", field: "trip_title" },
            { label: "Package", field: "package_title" },
            { label: "Merchant", field: "merchant_name" },
            { label: "Trip Logistics", field: "component_name" },
            { label: "Departure Date", field: "departure_date", renderValue: formatDate },
            { label: "Departure City", field: "departure_city" },
            { label: "Pricing Model", field: "pricing_model", renderValue: formatEnumLabel },
            { label: "Currency", field: "currency" },
            { label: "Date From", field: "season_from", renderValue: formatDate },
            { label: "Date To", field: "season_to", renderValue: formatDate },
            { label: "Merchant Cost", field: "cost", renderValue: formatMoney("currency") },
            { label: "Status", field: "status" },
            { label: "Allocation Priority", field: "allocation_priority" },
            { label: "Active", field: "is_active", renderValue: formatBool },
            { label: "Rejection Reason", field: "rejection_reason" },
            { label: "Rejected On", field: "rejected_on", renderValue: formatDate },
        ],
        relatedRecords: [
            {
                key: "merchant_order_lines",
                title: "Merchant Order Lines",
                emptyMessage: "No merchant order lines linked to this allocation.",
                primaryField: "name",
                getSecondary: (item) =>
                    item.pax_count != null ? `${item.pax_count} pax` : item.status || null,
                getDetailPath: (id) => `/merchant-order-line/${id}`,
                allowAdd: false,
            },
        ],
        columns: (navigate) => [
            { Header: "Name", accessor: "name", width: "16%" },
            {
                Header: "Destination",
                accessor: "trip_title",
                width: "14%",
                Cell: tripLinkCell(navigate, "allocations"),
            },
            { Header: "Merchant", accessor: "merchant_name", width: "14%" },
            { Header: "Trip Logistics", accessor: "component_name", width: "14%" },
            {
                Header: "Departure",
                accessor: "departure_date",
                width: "12%",
                Cell: ({ value }) => formatDate(value),
            },
            { Header: "Pricing", accessor: "pricing_model", width: "10%", Cell: ({ value }) => formatEnumLabel(value) },
            { Header: "Status", accessor: "status", width: "10%" },
            { Header: "Created", accessor: "created_at", width: "10%", Cell: ({ value }) => formatDate(value) },
        ],
    },
};

export const getTripEntityConfig = (entityKey) => TRIP_ENTITY_CONFIG[entityKey] || null;
